/**
 * Phase 3 — "Follow-up reminders" card on the deal page (Summary tab).
 * Own reminders for this deal: add (date + time + note), mark done / dismiss, past-due rows highlighted.
 * Reminders belong to whoever set them (deals carry no assigned rep); admins also see other users' reminders
 * on the deal, labelled, so a handover is visible.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Bell, Check, Clock, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { bucketOf, daysFromToday, fmtDue, sortForList, toDueIso } from './reminderModel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const RemindersCard = ({ deal, getAuthHeaders, isAdmin, currentUserId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState('09:00');
  const [note, setNote] = useState('');
  const [showClosed, setShowClosed] = useState(false);

  const load = useCallback(async () => {
    if (!deal?.id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reminders`, {
        headers: getAuthHeaders(),
        params: { deal_id: deal.id, status: 'all', mine: !isAdmin },
      });
      setRows(res.data?.reminders || []);
    } catch (e) {
      toast.error('Could not load reminders');
    } finally {
      setLoading(false);
    }
  }, [deal?.id, getAuthHeaders, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const due = toDueIso(date, time);
    if (!due) { toast.error('Pick a date and time'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/reminders`, { deal_id: deal.id, due_at: due, note: note.trim() }, { headers: getAuthHeaders() });
      setNote('');
      toast.success('Reminder set');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save reminder');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reminders/${id}`, { status }, { headers: getAuthHeaders() });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update reminder');
    }
  };

  const open = useMemo(() => sortForList(rows.filter((r) => r.status === 'pending')), [rows]);
  const closed = useMemo(() => rows.filter((r) => r.status !== 'pending'), [rows]);

  return (
    <Card className="p-4" data-testid="reminders-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-900">Follow-up reminders</span>
          {open.length > 0 && <span className="text-xs text-slate-500">({open.length} open)</span>}
        </div>
        {closed.length > 0 && (
          <button type="button" className="text-xs text-slate-500 hover:text-slate-800" onClick={() => setShowClosed((v) => !v)}>
            {showClosed ? 'Hide closed' : `Show closed (${closed.length})`}
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-4">
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">Date</label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-[150px]" />
        </div>
        <div>
          <label className="block text-[11px] text-slate-500 mb-1">Time</label>
          <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="h-8 w-[110px]" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] text-slate-500 mb-1">Note</label>
          <Input value={note} placeholder="e.g. call merchant about missed pull" maxLength={2000}
            onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} className="h-8" />
        </div>
        <Button size="sm" onClick={add} disabled={saving} className="h-8">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>

      {loading ? (
        <div className="text-xs text-slate-400">Loading…</div>
      ) : open.length === 0 ? (
        <div className="text-xs text-slate-400">No open reminders on this deal.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {open.map((r) => {
            const bucket = bucketOf(r);
            const days = daysFromToday(r);
            const mine = !currentUserId || r.user_id === currentUserId;
            return (
              <li key={r.id} className={`flex items-start justify-between gap-3 py-2 ${bucket === 'overdue' ? 'bg-red-50 -mx-2 px-2 rounded' : ''}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <Clock className={`h-3.5 w-3.5 ${bucket === 'overdue' ? 'text-red-600' : bucket === 'today' ? 'text-amber-600' : 'text-slate-400'}`} />
                    <span className={`tabular-nums ${bucket === 'overdue' ? 'text-red-700 font-semibold' : 'text-slate-700'}`}>{fmtDue(r.due_at)}</span>
                    {bucket === 'overdue' && <span className="text-[11px] text-red-700">{days} day{days === 1 ? '' : 's'} overdue</span>}
                    {bucket === 'today' && <span className="text-[11px] text-amber-700">today</span>}
                    {!mine && <span className="text-[11px] text-slate-400">another user's</span>}
                  </div>
                  <div className="text-sm text-slate-800 mt-0.5 break-words">{r.note || <span className="text-slate-400">(no note)</span>}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" variant="ghost" title="Done" onClick={() => setStatus(r.id, 'done')}><Check className="h-4 w-4 text-emerald-600" /></Button>
                  <Button size="sm" variant="ghost" title="Dismiss" onClick={() => setStatus(r.id, 'dismissed')}><X className="h-4 w-4 text-slate-500" /></Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showClosed && closed.length > 0 && (
        <ul className="mt-3 pt-2 border-t border-slate-100 divide-y divide-slate-100">
          {closed.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-1.5 text-xs text-slate-500">
              <span className="truncate"><span className="tabular-nums">{fmtDue(r.due_at)}</span> · {r.note || '(no note)'}</span>
              <span className="flex items-center gap-2 shrink-0">
                <span className={r.status === 'done' ? 'text-emerald-600' : 'text-slate-400'}>{r.status}</span>
                <button type="button" className="underline hover:text-slate-800" onClick={() => setStatus(r.id, 'pending')}>reopen</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default RemindersCard;
